import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { getEstimatedListingPrice, getGmpPercentage } from "@/lib/gmpUtils";
import { formatDisplayDate } from "@/lib/dateFormat";
import { formatInr, formatIssueSizeCr, formatSignedInr } from "@/lib/numberFormat";

const tabs = ["All", "Upcoming", "Ongoing", "Closing Today", "Allotment", "Allotted", "Listing Today", "Listed", "Closed"] as const;

const IPOList = () => {
  const { ipos } = useData();
  const [activeTab, setActiveTab] = useState<string>("All");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const statusFiltered =
    activeTab === "All" ? ipos : ipos.filter((ipo) => ipo.status.replace("_", " ").toLowerCase() === activeTab.toLowerCase());

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filtered = !normalizedSearch
    ? statusFiltered
    : statusFiltered.filter((ipo) => {
        const searchHaystack = [
          ipo.company,
          ipo.status,
          ipo.issueSize,
          ipo.priceBand,
          ipo.subscriptionStatus,
          ipo.exchange,
          ipo.openDate,
          ipo.closeDate,
          ipo.allotmentDate,
          ipo.listingDate,
          formatDisplayDate(ipo.openDate),
          formatDisplayDate(ipo.closeDate),
          ipo.allotmentDate ? formatDisplayDate(ipo.allotmentDate) : "",
          ipo.listingDate ? formatDisplayDate(ipo.listingDate) : "",
        ]
          .join(" ")
          .toLowerCase();

        return searchHaystack.includes(normalizedSearch);
      });

  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedIpos = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="py-6 sm:py-10 md:py-14">
      <div className="container mx-auto px-4">
        <h1 className="section-title mb-2 text-xl sm:text-2xl md:text-3xl">IPO Information</h1>
        <p className="text-muted-foreground mb-6 sm:mb-8 max-w-xl text-sm sm:text-base">Track upcoming, ongoing, and closed IPOs with live GMP, subscription status, and listing details.</p>

        <form onSubmit={onSearchSubmit} className="mb-6 sm:mb-8 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by company, status, category, date..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
          >
            <Search size={15} />
            Search
          </button>
        </form>

        <div className="flex gap-1 sm:gap-2 mb-6 sm:mb-8 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
              }}
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${activeTab === tab ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="finance-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm">Company</th>
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm">Price Band (₹)</th>
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm">Issue Size (₹ cr)</th>
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm hidden sm:table-cell">Open</th>
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm hidden sm:table-cell">Close</th>
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm">Allotment</th>
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm">Listing Date</th>
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm hidden md:table-cell">Status</th>
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm hidden lg:table-cell">GMP (₹)</th>
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm hidden xl:table-cell">Est. Listing Price (₹)</th>
                </tr>
              </thead>
              <tbody>
                {paginatedIpos.map((ipo, i) => (
                  <motion.tr key={ipo.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-2 sm:px-4 py-2 sm:py-3"><Link to={`/ipos/${ipo.id}`} className="font-medium text-foreground hover:text-secondary transition-colors text-xs sm:text-sm">{ipo.company}</Link></td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-muted-foreground text-xs sm:text-sm">₹{ipo.priceBand}</td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-muted-foreground text-xs sm:text-sm">{formatIssueSizeCr(ipo.issueSize)}</td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-muted-foreground text-xs sm:text-sm hidden sm:table-cell">{formatDisplayDate(ipo.openDate)}</td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-muted-foreground text-xs sm:text-sm hidden sm:table-cell">{formatDisplayDate(ipo.closeDate)}</td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-muted-foreground text-xs sm:text-sm">{ipo.allotmentDate ? formatDisplayDate(ipo.allotmentDate) : "-"}</td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-muted-foreground text-xs sm:text-sm">{ipo.listingDate ? formatDisplayDate(ipo.listingDate) : "-"}</td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell">
                      <span className={
                        ipo.status === "upcoming" ? "badge-info" :
                        ipo.status === "ongoing" ? "badge-success" :
                        ipo.status === "closing_today" ? "badge-warning" :
                        ipo.status === "allotment" ? "badge-secondary" :
                        ipo.status === "allotted" ? "badge-success" :
                        ipo.status === "listing_today" ? "badge-primary" :
                        ipo.status === "listed" ? "badge-success" :
                        ipo.status === "closed" ? "badge-destructive" :
                        "badge-warning"
                      }>
                        {ipo.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className={`px-2 sm:px-4 py-2 sm:py-3 font-semibold text-xs sm:text-sm hidden lg:table-cell ${ipo.gmp >= 0 ? "text-success" : "text-destructive"}`}>{formatSignedInr(ipo.gmp)} <span className="text-xs font-medium">({getGmpPercentage(ipo.gmp, ipo.priceBand)}%)</span></td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 font-semibold text-foreground text-xs sm:text-sm hidden xl:table-cell">{formatInr(getEstimatedListingPrice(ipo.priceBand, ipo.gmp, ipo.estimatedListing))}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No IPO records found for this search/filter.
          </div>
        )}
        {filtered.length > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
                disabled={safePage === 1}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 rounded-lg text-xs border ${
                    safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        <div className="mt-8 p-4 rounded-lg bg-muted/50 border border-border">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong>Disclaimer:</strong> IPO Grey Market Premium (GMP) information shared on this website is based on market sources, industry discussions, and publicly available information. GMP is unofficial, not regulated by SEBI, and may change without notice. We do not guarantee accuracy or future returns.
          </p>
        </div>
      </div>
    </div>
  );
};

export default IPOList;

